import React, { useMemo, useState } from 'react';

import dayjs from '~/timeInit';

import {
  Alert,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';

import getErrorMessage from '~/utils/getErrorMessage';

import { useDealInformationTabCount } from '../../DealInformationTabCounts';
import TabPanelShell from '../../shared/TabPanelShell';
import { UNMATCHED_ROW_ID, toggleExpandedRow } from '../surcharges/surchargeTabLayout';
import type { RoomRateSelection } from '../surcharges/surchargeUtils';

import BlackoutTableHeader from './BlackoutTableHeader';
import SalesforceBlackoutRow from './SalesforceBlackoutRow';
import UnmatchedBlackoutSection from './UnmatchedBlackoutSection';
import {
  buildAppliedRoomsByBlackoutId,
  buildRoomRateBlackoutAssignments,
  getUnmatchedRoomRateBlackoutAssignments,
} from './appliedBlackoutUtils';
import { classifyDaysScope, classifyRoomsScope } from './blackoutScopeClassifier';
import type { SalesforceBlackoutDate } from './types';

const BLACKOUT_TABLE_COLUMN_COUNT = 5;

interface Props {
  blackoutDates: Array<SalesforceBlackoutDate>;
  blackoutDatesByRoomRate: Record<string, Array<App.BlackoutDate>>;
  blackoutDatesByRoomRateError?: unknown;
  error?: unknown;
  isLoading: boolean;
  isLoadingBlackoutDatesByRoomRate: boolean;
  propertyId?: string;
  roomRateSelections: Array<RoomRateSelection>;
  roomTypeNames: Array<string>;
  vendorId?: string;
}

export default function BlackoutDatesTab({
  blackoutDates,
  blackoutDatesByRoomRate,
  blackoutDatesByRoomRateError,
  error,
  isLoading,
  isLoadingBlackoutDatesByRoomRate,
  propertyId,
  roomRateSelections,
  roomTypeNames,
  vendorId,
}: Props) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  useDealInformationTabCount('blackout-dates', isLoading || error ? undefined : blackoutDates.length);
  const now = dayjs.tz();

  const salesforceRows = useMemo(
    () =>
      blackoutDates.map((blackoutDate) => ({
        blackoutDate,
        days: classifyDaysScope(blackoutDate.name),
        rooms: classifyRoomsScope(blackoutDate.name, roomTypeNames),
      })),
    [blackoutDates, roomTypeNames],
  );

  const roomRateBlackoutAssignments = useMemo(
    () =>
      buildRoomRateBlackoutAssignments({
        blackoutDates,
        blackoutDatesByRoomRate,
        propertyId,
        roomRateSelections,
        roomTypeNames,
        vendorId,
      }),
    [blackoutDates, blackoutDatesByRoomRate, propertyId, roomRateSelections, roomTypeNames, vendorId],
  );

  const appliedRoomsByBlackoutId = useMemo(
    () => buildAppliedRoomsByBlackoutId(roomRateBlackoutAssignments),
    [roomRateBlackoutAssignments],
  );

  const unmatchedRoomRateBlackoutAssignments = useMemo(
    () => getUnmatchedRoomRateBlackoutAssignments(roomRateBlackoutAssignments),
    [roomRateBlackoutAssignments],
  );

  if (isLoading) {
    return (
      <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
        <CircularProgress />
        <Typography color="text.secondary">Loading blackout dates.</Typography>
      </Stack>
    );
  }

  if (error) {
    return <Alert severity="error">{getErrorMessage(error, 'Unable to load Salesforce blackout dates.')}</Alert>;
  }

  return (
    <TabPanelShell title="Blackout Dates" contentSx={{ p: 0 }}>
      <Stack spacing={0.5} sx={{ px: 3, pt: 3, pb: 1.5 }}>
        <Typography color="text.secondary" variant="body2">
          Expand a Salesforce blackout date to see the room rates with a matching blackout block.
        </Typography>
        <Typography color="text.secondary" variant="caption">
          * Rooms and Days are attempted extractions from the blackout Name text and may be incorrect — verify in
          Salesforce if unsure.
        </Typography>
      </Stack>
      <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
        <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
          <colgroup>
            <col style={{ width: 48 }} />
            <col />
            <col style={{ width: 220 }} />
            <col style={{ width: 210 }} />
            <col style={{ width: 200 }} />
          </colgroup>
          <BlackoutTableHeader columnCount={BLACKOUT_TABLE_COLUMN_COUNT} />
          <TableBody>
            {salesforceRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={BLACKOUT_TABLE_COLUMN_COUNT}>
                  <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                    No Salesforce blackout dates found for this opportunity.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {salesforceRows.map(({ blackoutDate, days, rooms }) => {
              const blackoutId = blackoutDate.sfid;
              const matchingAssignments = appliedRoomsByBlackoutId[blackoutId] ?? [];

              return (
                <SalesforceBlackoutRow
                  key={blackoutId}
                  blackoutDate={blackoutDate}
                  blackoutDatesByRoomRateError={blackoutDatesByRoomRateError}
                  columnCount={BLACKOUT_TABLE_COLUMN_COUNT}
                  days={days}
                  isExpanded={expandedRows.has(blackoutId)}
                  matchingAssignments={matchingAssignments}
                  now={now}
                  rooms={rooms}
                  onToggleExpanded={() => setExpandedRows((current) => toggleExpandedRow(current, blackoutId))}
                />
              );
            })}
            <UnmatchedBlackoutSection
              assignments={unmatchedRoomRateBlackoutAssignments}
              columnCount={BLACKOUT_TABLE_COLUMN_COUNT}
              error={blackoutDatesByRoomRateError}
              isExpanded={expandedRows.has(UNMATCHED_ROW_ID)}
              isLoading={isLoadingBlackoutDatesByRoomRate}
              now={now}
              onToggleExpanded={() => setExpandedRows((current) => toggleExpandedRow(current, UNMATCHED_ROW_ID))}
            />
          </TableBody>
        </Table>
      </TableContainer>
    </TabPanelShell>
  );
}
